import React, { useState, useEffect, useCallback, useMemo, memo } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  Users,
  UserCheck,
  DollarSign,
  CheckCircle,
  Activity,
  Calendar,
  ArrowUpRight,
  ArrowDownRight,
  Loader2,
  Eye,
  UserPlus,
  CreditCard,
  AlertCircle,
  BarChart3,
  PieChart as PieChartIcon,
  CalendarPlus,
  XCircle,
  TrendingUp,
  Sparkles,
} from "lucide-react";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "../components/ui/chart";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { getDoctorStats } from "../services/doctorService";
import { getPatientStats } from "../services/patientService";
import { getRecentActivity, timeAgo } from "../services/activityService";
import StatStrip from "../components/common/StatStrip";
import RevenueChart from "../components/dashboard/RevenueChart";
import ManualBookingDialog from "../components/consultations/ManualBookingDialog";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import LoadingSpinner from "../components/common/LoadingSpinner";

// ─── Staggered fade-up helper ────────────────────────────────────────────────
const FadeUp = ({ delay = 0, children, style }) => (
  <motion.div
    initial={{ opacity: 0, y: 18 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.45, delay, ease: [0.22, 1, 0.36, 1] }}
    style={style}
  >
    {children}
  </motion.div>
);


// ─── Stat Card ────────────────────────────────────────────────────────────────
const StatCard = memo(({ card, index }) => {
  const Icon = card.icon;
  return (
    <FadeUp delay={0.08 + index * 0.07}>
      <div className="admin-card lift" style={{ padding: "20px 22px", cursor: "default" }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ margin: 0, fontSize: 13, fontWeight: 500, color: "hsl(var(--muted-foreground))" }}>
              {card.title}
            </p>
            <p style={{ margin: "8px 0 6px", fontSize: 30, fontWeight: 700, color: "hsl(var(--foreground))", letterSpacing: "-0.5px", lineHeight: 1 }}>
              {card.value.toLocaleString()}
            </p>
            <p style={{ margin: "0 0 10px", fontSize: 12, color: "hsl(var(--muted-foreground))" }}>{card.description}</p>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px", borderRadius: 20, background: card.trend === "up" ? "hsl(var(--tint-green))" : "hsl(var(--tint-red))", fontSize: 11.5, fontWeight: 600, color: card.trend === "up" ? "hsl(var(--on-green))" : "hsl(var(--on-red))" }}>
              {card.trend === "up" ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
              {card.change} this month
            </div>
          </div>

          <div style={{
            width: 44, height: 44, borderRadius: 12,
            background: "hsl(var(--accent))",
            display: "flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0,
          }}>
            <Icon size={20} color="hsl(var(--primary))" />
          </div>
        </div>
      </div>
    </FadeUp>
  );
});
StatCard.displayName = "StatCard";

// ─── Activity item ────────────────────────────────────────────────────────────
const activityColors = { success: "#10b981", warning: "#f59e0b", info: "#3b82f6" };

const ActivityItem = ({ activity, index }) => {
  const Icon = activity.icon;
  return (
    <motion.div
      initial={{ opacity: 0, x: -14 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3, delay: 0.5 + index * 0.07 }}
      style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "11px 0", borderBottom: "1px solid hsl(var(--muted))" }}
    >
      <div style={{
        width: 34, height: 34, borderRadius: 10, flexShrink: 0,
        background: `${activityColors[activity.type]}18`,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <Icon size={15} style={{ color: activityColors[activity.type] }} />
      </div>
      <div style={{ flex: 1 }}>
        <p style={{ margin: 0, fontSize: 12.5, fontWeight: 600, color: "hsl(var(--foreground))" }}>{activity.action}</p>
        <p style={{ margin: "1px 0 0", fontSize: 11.5, color: "hsl(var(--muted-foreground))" }}>{activity.user}</p>
      </div>
      <span style={{ fontSize: 10.5, color: "hsl(var(--muted-foreground))", whiteSpace: "nowrap", marginTop: 2 }}>{activity.time}</span>
    </motion.div>
  );
};

// ─── Quick Action Button ───────────────────────────────────────────────────────
const QuickAction = ({ icon: Icon, label, hint, tone, onClick }) => (
  <button
    onClick={onClick}
    className="group"
    style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", borderRadius: 14, border: "1px solid hsl(var(--border))", background: "hsl(var(--card))", cursor: "pointer", textAlign: "left", transition: "all 0.18s", fontFamily: "inherit" }}
    onMouseEnter={e => { e.currentTarget.style.transform = "translateX(3px)"; e.currentTarget.style.borderColor = `hsl(${tone} / 0.5)`; e.currentTarget.style.background = `hsl(${tone} / 0.08)`; }}
    onMouseLeave={e => { e.currentTarget.style.transform = ""; e.currentTarget.style.borderColor = ""; e.currentTarget.style.background = "hsl(var(--card))"; }}
  >
    <span style={{ width: 38, height: 38, borderRadius: 11, flexShrink: 0, background: `hsl(${tone})`, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <Icon size={17} color="white" />
    </span>
    <span style={{ flex: 1, minWidth: 0 }}>
      <span style={{ display: "block", fontSize: 13, fontWeight: 600, color: "hsl(var(--foreground))" }}>{label}</span>
      <span style={{ display: "block", fontSize: 11.5, color: "hsl(var(--muted-foreground))" }}>{hint}</span>
    </span>
    <ArrowUpRight size={15} style={{ color: "hsl(var(--muted-foreground))" }} />
  </button>
);

// ─── Main Dashboard ───────────────────────────────────────────────────────────
const DashboardShadcn = memo(() => {
  const [stats, setStats] = useState({ doctors: 0, patients: 0, withdrawals: 0, verifications: 0 });
  const [doctorStats, setDoctorStats] = useState(null);
  const [timeRange, setTimeRange] = useState("6months");
  const [showManualBookingDialog, setShowManualBookingDialog] = useState(false);
  const [activities, setActivities] = useState(null);
  const navigate = useNavigate();

  // The page renders immediately; each number fills in as its query returns, so one slow
  // or failing query can never leave the whole dashboard on a spinner.
  const loadDashboardData = useCallback(() => {
    getDoctorStats()
      .then((r) => {
        if (r?.success) {
          setDoctorStats(r.data);
          setStats((prev) => ({ ...prev, doctors: r.data.total, verifications: r.data.verified }));
        }
      })
      .catch((e) => console.error("Dashboard: doctor stats failed", e));
    getPatientStats()
      .then((r) => {
        if (r?.success) setStats((prev) => ({ ...prev, patients: r.data.total }));
      })
      .catch((e) => console.error("Dashboard: patient stats failed", e));
    setStats((prev) => ({ ...prev, withdrawals: 23 }));
  }, []);

  useEffect(() => { loadDashboardData(); }, [loadDashboardData]);
  useEffect(() => {
    let alive = true;
    getRecentActivity(8).then((list) => { if (alive) setActivities(list); }).catch(() => { if (alive) setActivities([]); });
    return () => { alive = false; };
  }, []);

  const statCards = useMemo(() => [
    { title: "Total Doctors", value: stats.doctors, icon: UserCheck, description: "Active medical professionals", change: "+12%", trend: "up", borderColor: "hsl(var(--input))", shadowColor: "rgba(59,130,246,0.15)", blobColor: "hsl(var(--tint-blue))", iconGradientFrom: "#3b82f6", iconGradientTo: "#6366f1" },
    { title: "Total Patients", value: stats.patients, icon: Users, description: "Registered patients", change: "+8%", trend: "up", borderColor: "hsl(var(--tint-blue-border))", shadowColor: "rgba(6,182,212,0.15)", blobColor: "hsl(var(--tint-blue))", iconGradientFrom: "#0ea5e9", iconGradientTo: "#06b6d4" },
    { title: "Pending Withdrawals", value: stats.withdrawals, icon: DollarSign, description: "Awaiting approval", change: "-3%", trend: "down", borderColor: "hsl(var(--tint-green-border))", shadowColor: "rgba(16,185,129,0.15)", blobColor: "hsl(var(--tint-green))", iconGradientFrom: "#10b981", iconGradientTo: "#059669" },
    { title: "Verifications", value: stats.verifications, icon: CheckCircle, description: "Verified accounts", change: "+15%", trend: "up", borderColor: "hsl(var(--tint-violet-border))", shadowColor: "rgba(99,102,241,0.15)", blobColor: "hsl(var(--tint-violet))", iconGradientFrom: "#6366f1", iconGradientTo: "#8b5cf6" },
  ], [stats]);

  const activityIcons = { booking: CalendarPlus, completed: CheckCircle, cancelled: XCircle, doctor: UserPlus, withdrawal: CreditCard };
  const recentActivities = useMemo(
    () => (activities || []).map((e) => ({ id: e.id, action: e.action, user: e.detail, time: timeAgo(e.at), type: e.type, icon: activityIcons[e.kind] || Activity })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activities]
  );

  return (
    <div style={{ maxWidth: 1280, margin: "0 auto" }}>

      {/* Page Header */}
      <FadeUp delay={0}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 26, flexWrap: "wrap", gap: 12 }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
              <div style={{ width: 28, height: 28, borderRadius: 8, background: "hsl(var(--primary))", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Sparkles size={14} color="white" />
              </div>
              <h1 style={{ margin: 0, fontSize: 24, fontWeight: 600, color: "hsl(var(--foreground))", letterSpacing: "-0.5px" }}>
                Dashboard Overview
              </h1>
            </div>
            <p style={{ margin: 0, fontSize: 13, color: "hsl(var(--muted-foreground))" }}>
              Welcome back! Here's what's happening with Soocher today.
            </p>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Select value={timeRange} onValueChange={setTimeRange}>
              <SelectTrigger style={{ width: 150, height: 36, borderRadius: 10, border: "1.5px solid hsl(var(--input))", background: "hsl(var(--card))", fontSize: 12.5, fontFamily: "IBM Plex Sans, sans-serif" }}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7days">Last 7 days</SelectItem>
                <SelectItem value="30days">Last 30 days</SelectItem>
                <SelectItem value="3months">Last 3 months</SelectItem>
                <SelectItem value="6months">Last 6 months</SelectItem>
                <SelectItem value="1year">Last year</SelectItem>
              </SelectContent>
            </Select>
            <Button
              onClick={() => { }}
              style={{ height: 36, borderRadius: 10, background: "hsl(var(--primary))", color: "white", border: "none", fontSize: 12.5, fontWeight: 600, fontFamily: "IBM Plex Sans, sans-serif", cursor: "pointer", display: "flex", alignItems: "center", gap: 6, padding: "0 14px", boxShadow: "0 4px 12px rgba(59,130,246,0.3)" }}
            >
              <Eye size={14} /> View Reports
            </Button>
          </div>
        </div>
      </FadeUp>

      {/* Revenue & Growth */}
      <FadeUp delay={0.3}>
        <div style={{ marginBottom: 22 }}>
          <RevenueChart range={timeRange} />
        </div>
      </FadeUp>

      {/* Stat Cards */}
      <StatStrip
        min={220}
        style={{ marginBottom: 22 }}
        items={statCards.map((c, i) => ({
          label: c.title, value: Number(c.value || 0).toLocaleString("en-IN"), icon: c.icon, hint: c.description,
          tone: ["blue", "violet", "amber", "green"][i % 4],
          badge: { text: `${c.change} this month`, up: c.trend === "up" },
        }))}
      />

      {/* Activity + Quick Actions */}
      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 16 }}>

        {/* Recent Activity */}
        <FadeUp delay={0.42}>
          <div className="admin-card" style={{ padding: "20px 22px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
              <Activity size={15} color="#3b82f6" />
              <span style={{ fontSize: 14, fontWeight: 700, color: "hsl(var(--foreground))" }}>Recent Activity</span>
              
            </div>
            {activities === null ? (
              <p style={{ margin: "18px 0", fontSize: 12.5, color: "hsl(var(--muted-foreground))" }}>Loading recent activity…</p>
            ) : recentActivities.length === 0 ? (
              <p style={{ margin: "18px 0", fontSize: 12.5, color: "hsl(var(--muted-foreground))" }}>No recent activity yet.</p>
            ) : (
              recentActivities.map((a, i) => <ActivityItem key={a.id} activity={a} index={i} />)
            )}
          </div>
        </FadeUp>

        {/* Quick Actions */}
        <FadeUp delay={0.48}>
          <div className="admin-card" style={{ padding: "20px 22px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
              <BarChart3 size={15} color="#6366f1" />
              <span style={{ fontSize: 14, fontWeight: 700, color: "hsl(var(--foreground))" }}>Quick Actions</span>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 8 }}>
              <QuickAction icon={CalendarPlus} label="Manual Booking" hint="Book for a patient" tone="217 91% 60%" onClick={() => setShowManualBookingDialog(true)} />
              <QuickAction icon={UserCheck} label="Verify Doctor" hint="Review pending doctors" tone="152 62% 42%" onClick={() => navigate("/doctors/pending")} />
              <QuickAction icon={DollarSign} label="Withdrawals" hint="Approve payout requests" tone="38 92% 50%" onClick={() => navigate("/withdrawals")} />
              <QuickAction icon={Users} label="All Patients" hint="Browse registered patients" tone="262 80% 62%" onClick={() => navigate("/patients/all")} />
              <QuickAction icon={Calendar} label="Consultations" hint="All bookings & sessions" tone="330 75% 58%" onClick={() => navigate("/consultations/all")} />
            </div>
          </div>
        </FadeUp>
      </div>

      <ManualBookingDialog
        open={showManualBookingDialog}
        onOpenChange={setShowManualBookingDialog}
        onSuccess={() => console.log("Manual booking created successfully")}
      />
    </div>
  );
});

DashboardShadcn.displayName = "DashboardShadcn";
export default DashboardShadcn;
